const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

window.addEventListener('DOMContentLoaded', () => initializeScrollAnimationTrigger());

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

function horizontalLoop(items, config) {
  items = gsap.utils.toArray(items);
  config = config || {};
  let tl = gsap.timeline({
    repeat: config.repeat,
    paused: config.paused,
    defaults: { ease: "none" },
    onReverseComplete: () => tl.totalTime(tl.rawTime() + tl.duration() * 100)
  }),
    length = items.length,
    startX = items[0].offsetLeft,
    times = [],
    widths = [],
    xPercents = [],
    curIndex = 0,
    pixelsPerSecond = (config.speed || 1) * 100,
    snap = config.snap === false ? (v) => v : gsap.utils.snap(config.snap || 1), // some browsers shift by a pixel to accommodate flex layouts, so for example if width is 20% the first element's width might be 242px, and the next 243px, alternating back and forth. So we snap to 5 percentage points to make things look more natural
    populateWidths = () =>
      items.forEach((el, i) => {
        widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));
        xPercents[i] = snap(
          (parseFloat(gsap.getProperty(el, "x", "px")) / widths[i]) * 100 +
          gsap.getProperty(el, "xPercent")
        );
      }),
    getTotalWidth = () =>
      items[length - 1].offsetLeft +
      (xPercents[length - 1] / 100) * widths[length - 1] -
      startX +
      items[length - 1].offsetWidth *
      gsap.getProperty(items[length - 1], "scaleX") +
      (parseFloat(config.paddingRight) || 0),
    totalWidth,
    curX,
    distanceToStart,
    distanceToLoop,
    item,
    i;
  populateWidths();
  gsap.set(items, {
    // convert "x" to "xPercent" to make things responsive, and populate the widths/xPercents Arrays to make lookups faster.
    xPercent: (i) => xPercents[i]
  });
  gsap.set(items, { x: 0 });
  totalWidth = getTotalWidth();
  for (i = 0; i < length; i++) {
    item = items[i];
    curX = (xPercents[i] / 100) * widths[i];
    distanceToStart = item.offsetLeft + curX - startX;
    distanceToLoop =
      distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");
    tl.to(
      item,
      {
        xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
        duration: distanceToLoop / pixelsPerSecond
      },
      0
    )
      .fromTo(
        item,
        {
          xPercent: snap(
            ((curX - distanceToLoop + totalWidth) / widths[i]) * 100
          )
        },
        {
          xPercent: xPercents[i],
          duration:
            (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
          immediateRender: false
        },
        distanceToLoop / pixelsPerSecond
      )
      .add("label" + i, distanceToStart / pixelsPerSecond);
    times[i] = distanceToStart / pixelsPerSecond;
  }
  function toIndex(index, vars) {
    vars = vars || {};
    Math.abs(index - curIndex) > length / 2 &&
      (index += index > curIndex ? -length : length); // always go in the shortest direction
    let newIndex = gsap.utils.wrap(0, length, index),
      time = times[newIndex];
    if (time > tl.time() !== index > curIndex) {
      // if we're wrapping the timeline's playhead, make the proper adjustments
      vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
      time += tl.duration() * (index > curIndex ? 1 : -1);
    }
    curIndex = newIndex;
    vars.overwrite = true;
    return tl.tweenTo(time, vars);
  }
  tl.next = (vars) => {
    toIndex(curIndex + 1, vars) && setMiddle(tl.current() + 2);
  };
  tl.previous = (vars) => {
    toIndex(curIndex - 1, vars) && setMiddle(tl.current() + 2);
  };
  tl.current = () => curIndex;
  tl.toIndex = (index, vars) => toIndex(index, vars);
  tl.updateIndex = () => (curIndex = Math.round(tl.progress() * items.length));
  tl.times = times;
  tl.progress(1, true).progress(0, true); // pre-render for performance
  if (config.reversed) {
    tl.vars.onReverseComplete();
    tl.reverse();
  }
  if (config.draggable && typeof Draggable === "function") {
    let proxy = document.createElement("div"),
      wrap = gsap.utils.wrap(0, 1),
      ratio,
      startProgress,
      draggable,
      dragSnap,
      roundFactor,
      align = () =>
        tl.progress(
          wrap(startProgress + (draggable.startX - draggable.x) * ratio)
        ),
      syncIndex = () => tl.updateIndex();
    // typeof InertiaPlugin === "undefined" &&
    //   console.warn(
    //     "InertiaPlugin required for momentum-based scrolling and snapping. https://greensock.com/club"
    //   );
    draggable = Draggable.create(proxy, {
      trigger: items[0].parentNode,
      type: "x",
      onPress() {
        startProgress = tl.progress();
        tl.progress(0);
        populateWidths();
        totalWidth = getTotalWidth();
        ratio = 1 / totalWidth;
        dragSnap = totalWidth / items.length;
        roundFactor = Math.pow(
          10,
          ((dragSnap + "").split(".")[1] || "").length
        );
        tl.progress(startProgress);
      },
      onDrag: align,
      onThrowUpdate: align,
      inertia: true,
      snap: (value) => {
        let n =
          Math.round(parseFloat(value) / dragSnap) * dragSnap * roundFactor;
        return (n - (n % 1)) / roundFactor;
      },
      onRelease: syncIndex,
      onThrowComplete: () =>
        gsap.set(proxy, { x: 0 }) && syncIndex() && setMiddle(tl.current() + 2) //Find the middle item and toggle Class
    })[0];

    tl.draggable = draggable;
  }

  return tl;
}

class ProductBanner extends HTMLElement {
  constructor() {
    super()
    this.animate();
  }

  animate() {
    const elements = gsap.utils.toArray("product-banner .product-banner__item");
    const loop = horizontalLoop(elements, {
      paused: false,
      repeat: -1,
      draggable: true,
      speed: 1.5,
    });

     const container = document.querySelector(".product-banner__wrapper");

  container.addEventListener("mouseenter", () => loop.pause());
  container.addEventListener("mouseleave", () => loop.play());
  }
}
customElements.define('product-banner', ProductBanner);

class ProductFeatures extends HTMLElement {
  constructor() {
    super()
    this.animate();
  }

  animate() {
    gsap.set('.product-features__image:not(.start)', { autoAlpha: 0 });
    const images = gsap.utils.toArray('.product-features__image');
    const headlines = gsap.utils.toArray('.product-features__list ul li');
    const totalDuration = this.dataset.totalDuration;
    const singleDuration = totalDuration / headlines.length;

    ScrollTrigger.create({
      trigger: '.product-features__pin',
      start: 'center center',
      end: '+=' + totalDuration,
      pin: true,
      scrub: true
    });

    headlines.forEach((elem, i) => {
      const smallTimeline = gsap.timeline();
      const relevantImage = this.querySelector('.product-features__image-' + i);

      ScrollTrigger.create({
        trigger: ".product-features__wrapper",
        start: "top -=" + (singleDuration * i),
        end: "+=" + singleDuration,
        animation: smallTimeline,
        toggleActions: relevantImage.classList.contains('remaining') ? "play none play reverse" : "play reverse play reverse",
      });

      smallTimeline
        .to(elem, { duration: 0.25, opacity: 1, ease: 'none' }, 0)
        .set(relevantImage, { autoAlpha: 1 }, 0.125);
    });

  }
}
customElements.define('product-features', ProductFeatures);

class ProductUGC extends HTMLElement {
  constructor() {
    super()
    this._setup();
  }

  _setup() {
    const elements = gsap.utils.toArray("product-ugc .product-ugc__item");
    const loop = horizontalLoop(elements, {
      paused: false,
      repeat: -1,
      draggable: true,
      paddingRight: 20
    });
  }
}
customElements.define('product-ugc', ProductUGC);

class CollectionRowExpanding extends HTMLElement {
  constructor() {
    super()
    this.threshold = 990;
    this.columns = this.dataset.columns;
    this.viewAll = this.querySelector('.collection__row-view-all');
    this.elements = this.querySelectorAll('.collection__row-product');
    this.loop = undefined;
    this.mobileSlider = this.dataset.slider;
    this._setup();
  }

  _setup() {
    this.elements.forEach(element => {
      element.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
      element.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
    });

    if (this.mobileSlider) {
      let mm = gsap.matchMedia();
      mm.add("(max-width: 989px)", () => {
        this.loop = horizontalLoop(this.elements, {
          paused: false,
          repeat: -1,
          draggable: true,
          paddingRight: 20
        });
      
        return () => { // optional
          this.loop.kill();
        };
      });
    }
  }

  onMouseEnter(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseEnterDesktop(e);
    }
    return;
  }

  onMouseLeave(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseLeaveDesktop(e);
    }
    return;
  }

  onMouseEnterDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;

    e.target.style.width = '30%';
    for (let i = start; i < end; i++) {
      if (i != idx) {
        const element = this.elements[i];
        element.style.width = (100 - 30) / (this.columns - 1) + '%';
      }
    }
  }

  onMouseLeaveDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;
    
    for (let i = start; i < end; i++) {
      const element = this.elements[i];
      element.style.width = 100 / this.columns + '%';
    }
  }
}
customElements.define('collection-row-expanding', CollectionRowExpanding);

class OtherProducts extends HTMLElement {
  constructor() {
    super()
    this.threshold = 990;
    this.columns = this.dataset.columns;
    this.viewAll = this.querySelector('.collection__row-view-all');
    this.elements = this.querySelectorAll('.collection__row-product');
    this._setup();
  }

  _setup() {
    this.elements.forEach(element => {
      element.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
      element.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
    });
  }

  onMouseEnter(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseEnterDesktop(e);
    }
    return;
  }

  onMouseLeave(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseLeaveDesktop(e);
    }
    return;
  }

  onMouseEnterDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;

    e.target.style.width = '30%';
    for (let i = start; i < end; i++) {
      if (i != idx) {
        const element = this.elements[i];
        element.style.width = (100 - 30) / (this.columns - 1) + '%';
      }
    }
  }

  onMouseLeaveDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;
    
    for (let i = start; i < end; i++) {
      const element = this.elements[i];
      element.style.width = 100 / this.columns + '%';
    }
  }
}
customElements.define('other-products', OtherProducts);

class LightersTestExpanding extends HTMLElement {
  constructor() {
    super()
    this.threshold = 990;
    this.columns = this.dataset.columns;
    this.viewAll = this.querySelector('.collection__row-view-all');
    this.elements = this.querySelectorAll('.collection__row-product');
    this._setup();
  }

  _setup() {
    this.elements.forEach(element => {
      element.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
      element.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
    });
  }

  onMouseEnter(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseEnterDesktop(e);
    }
    return;
  }

  onMouseLeave(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseLeaveDesktop(e);
    }
    return;
  }

  onMouseEnterDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;

    e.target.style.width = '30%';
    for (let i = start; i < end; i++) {
      if (i != idx) {
        const element = this.elements[i];
        element.style.width = (100 - 30) / (this.columns - 1) + '%';
      }
    }
  }

  onMouseLeaveDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    const end = this.columns * (row + 1) * 1;
    
    for (let i = start; i < end; i++) {
      const element = this.elements[i];
      element.style.width = 100 / this.columns + '%';
    }
  }
}
customElements.define('lighters-test-expanding', LightersTestExpanding);

class FeaturedLightersOld extends HTMLElement {
  constructor() {
    super()
    this.lighters = this.querySelectorAll('.lighter');
    this.lighterStart = this.querySelector('.lighter.start');
    this.lighterCopy = this.querySelector('.lighter.copy');

    this.mode = window.outerWidth > 1200 ? "desktop" : "mobile";
    this.paths = {
      first: {
        desktop: {
          x: '25vw',
          y: '70vh',
        },
        mobile: {
          x: '0vw',
          y: '100vh',
        }
      },
      second: {
        desktop: {
          x: '-20vw',
          y: '180vh',
        },
        mobile: {
          x: '0vw',
          y: '200vh',
        }
      },
      third: {
        desktop: {
          x: '15vw',
          y: '290vh',
        },
        mobile: {
          x: '0vw',
          y: '290vh',
        }
      },
      dropping: {
        desktop: {
          x: '-100vw',
          y: '-100vh',
        },
        mobile: {
          // x: '-100%',
          // y: '-100vh',
          x: '-110vw',
          y: '-100vh',
        }
      },
      train: {
        desktop: {
          fromXPercent: 100,
          toXPercent: 0
        },
        mobile: {
          fromXPercent: 25,
          toXPercent: -25 
        }
      }
    }
    // if (this.mode === "desktop") {
    //   this.animate();
    // } else {
    //   this.animateMobile();
    // }
    this.animate();
  }

  animateMobile() {
    gsap.timeline({
      scrollTrigger: {
        trigger: ".firstStop",
        start: "top center",
        scrub: true,
        ease: 'none',
      }
    })
    .fromTo('.lighter__container .flame', {
      opacity: 0
    }, {
      opacity: 1,
      ease: 'power3.inOut',
    })
    .to('.lighter__container .flame', {
      opacity: 0,
    })
  }

  animate() {
    const h2 = this.querySelectorAll('.stop');
    h2.forEach(el => {
      gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          end: "+=200px",
          scrub: 1
        }
      })
        .from(
          el.querySelectorAll('h2'),
          {
            duration: 1,
            opacity: 0,
            y: 100,
            stragger: 1
          },
          1
        )
        .from(
          el.querySelectorAll('img'),
          {
            duration: 1,
            opacity: 0,
            y: 100,
            stragger: 1
          },
          1
        )
    });

    // Dropping & queue 
    gsap.timeline({
      scrollTrigger: {
        trigger: ".section-train",
        start: "top",
        end: '+=1000',
        scrub: true,
        ease: 'none',
      }
    })
    .from('.start__container', {
      motionPath: {
        path: [
          {
            x: this.paths.dropping[this.mode].x,
            y: this.paths.dropping[this.mode].y,
          }
        ]
      }
    }, 0)
    .fromTo('.train', {
      xPercent: this.paths.train[this.mode].fromXPercent
    }, {
      xPercent: this.paths.train[this.mode].toXPercent
    }, 0)

    // Jumping in each stop
    gsap.timeline({
      scrollTrigger: {
        trigger: ".train-inner",
        // trigger: ".section-train",
        start: "center+=200 center",
        // end: '+=400',
        scrub: true,
        ease: 'none',
      }
    })
    .to('.start__container', {
      rotate: 338,
      scale: 1.2,
      duration: 3,
      motionPath: {
        path: [
          {
            x: this.paths.first[this.mode].x,
            y: this.paths.first[this.mode].y,
          }
        ]
      },
      ease: 'none',
    })
    .fromTo('.start__container .start', {
      opacity: 1
    }, {
      duration: 2,
      opacity: 0,
      ease: 'none',
    }, 0)
    .fromTo('.start__container .firstCopy', {
      opacity: 0
    }, {
      duration: 2,
      opacity: 1,
      ease: 'none',
    }, 0)
    .fromTo('.start__container .flame', {
      opacity: 0
    }, {
      opacity: 1,
      ease: 'power3.inOut',
    })
    .to('.start__container .flame', {
      opacity: 0,
    }, '+=2')

    gsap.timeline({
      scrollTrigger: {
        trigger: '.firstStop h2',
        start: 'top+=100 center',
        end: '+=1000',
        scrub: true
      }
    })
    .to('.start__container', {
      ease: 'none',
      rotation: 379,
      duration: 2,
      motionPath: {
        path: [
          {
            x: this.paths.second[this.mode].x,
            y: this.paths.second[this.mode].y,
          }
        ]
      }
    }, "start")
    .fromTo('.start__container .secondCopy', {
      opacity: 0
    }, {
      opacity: 1,
      ease: 'none',
    }, 0)
    .fromTo('.start__container .poker', {
      yPercent: -5
    }, {
      yPercent: 25,
      delay: 0.2,
      ease: 'none',
    }, 0)
    .to('.start__container .poker', {
      yPercent: -5,
      ease: 'none',
    })

    gsap.timeline({
      scrollTrigger: {
        trigger: '.secondStop',
        start: 'center+=200 center',
        duration: 2,
        // end: 'bottom+=200',
        scrub: true
      }
    })
    .to('.start__container', {
      ease: 'none',
      rotation: 540,
      motionPath: {
        path: [
          {
            x: this.paths.third[this.mode].x,
            y: this.paths.third[this.mode].y,
          }
        ]
      }
    })
    .to('.start__container .secondCopy', {
      opacity: 0,
      ease: 'none',
    }, 0)
    .fromTo('.start__container .thirdCopy', {
      opacity: 0
    }, {
      opacity: 1,
      ease: 'none',
    }, 0)

    gsap.timeline({
      scrollTrigger: {
        trigger: '.thirdStop',
        start: 'center right',
        end: '+=200',
        // end: 'bottom+=200',
        scrub: true
      }
    })
    .fromTo('.start__container .refill', {
      opacity: 0
    }, {
      opacity: 1,
      ease: 'none',
    })

  }
}
customElements.define('featured-lighters-old', FeaturedLightersOld);

class CareFeatures extends HTMLElement {
  constructor() {
    super()
    this.animate();
  }

  animate() {
    gsap.set('.product-features__image:not(.start)', { autoAlpha: 0 });
    this.tabsButtons = document.querySelectorAll('.product-features__tabs-buttons button');
    this.tabs = document.querySelectorAll('.product-features__tab');
    this.subtitles = document.querySelectorAll('.subtitles .h4');

    for (let i = 0; i < this.tabsButtons.length; i++) {
      this.tabsButtons[i].addEventListener('click', (e) => {
        this.openTab(i);
      })
    }

    this.restart();
  }

  restart() {
    ScrollTrigger.killAll();
    const headlines = gsap.utils.toArray('.product-features__tab:not(.hide) .product-features__list ul li');
    const totalDuration = 2500;
    const singleDuration = totalDuration / headlines.length;

    gsap.set('.product-features__list li h3', {
      opacity: 0.2,
    });
    gsap.set('.product-features__list li p', {
      height: 0,
      overflow: 'hidden',
    });
    
    ScrollTrigger.create({
      trigger: '.product-features__pin',
      start: 'center center',
      end: '+=' + totalDuration,
      pin: true,
      scrub: true
    });

    headlines.forEach((elem, i) => {
      const smallTimeline = gsap.timeline();
      const relevantImage = this.querySelector('.product-features__image-' + elem.dataset.idx);

      if (relevantImage) {
        ScrollTrigger.create({
          trigger: ".product-features__wrapper",
          start: "top -=" + (singleDuration * i),
          end: "+=" + singleDuration,
          animation: smallTimeline,
          toggleActions: relevantImage.classList.contains('remaining') ? "play none play reverse" : "play reverse play reverse",
        });

        smallTimeline
          .to(elem.querySelector('h3'), { duration: 0.25, opacity: 1, ease: 'none' }, 0)
          .to(elem.querySelector('p'), { duration: 0.25, height: 'auto', ease: 'none' }, 0)
          .set(document.querySelectorAll('.product-features__tab:not(.hide) .product-features__image:not(.product-features__image-' + elem.dataset.idx + ')'), { opacity: 0 })
          .set(relevantImage, { autoAlpha: 1 }, 0.125);
      }
    });
  }

  hideAllTabs() {
    this.tabs.forEach(function(element) {
      element.classList.add('hide');
    });
  
    this.tabsButtons.forEach(function(element) {
      element.classList.remove('active');
    });
  }

  hideAllSubtitles() {
    this.subtitles.forEach(function(element) {
      element.classList.remove('active');
    });
  }
    
  openTab(i) {
    this.hideAllTabs();
    this.hideAllSubtitles();
    this.tabs[i].classList.remove('hide');
    this.tabsButtons[i].classList.add('active');
    this.subtitles[i].classList.add('active');
    this.restart();
  }

}
customElements.define('care-features', CareFeatures);

class AnimatedTitleMorphing extends HTMLElement {
  constructor() {
    super()
    this.texts = [
      "KEEP",
      "YOUR",
      "HEAD",
      "HIGH"
    ];
    this.morphTime = 1;
    this.cooldownTime = 0.25;
    this.textIndex = this.texts.length - 1;
    this.time = new Date();
    this.morph = 0;
    this.cooldown = this.cooldownTime;
    this.elts = {
      text1: document.getElementById("text1"),
      text2: document.getElementById("text2")
    };
    this.elts.text1.textContent = this.texts[this.textIndex % this.texts.length];
    this.elts.text2.textContent = this.texts[(this.textIndex + 1) % this.texts.length];
    // this.animate();
  }

  animate() {
    requestAnimationFrame(this.animate);

    let newTime = new Date();
    let shouldIncrementIndex = this.cooldown > 0;
    let dt = (newTime - this.time) / 1000;
    this.time = newTime;

    this.cooldown -= dt;

    if (this.cooldown <= 0) {
      if (shouldIncrementIndex) {
        this.textIndex++;
      }
      this.doMorph();
    } else {
      this.doCooldown();
    }
  }

  doMorph() {
    this.morph -= this.cooldown;
    this.cooldown = 0;

    let fraction = this.morph / this.morphTime;

    if (fraction > 1) {
      this.cooldown = cooldownTime;
      fraction = 1;
    }

    setMorph(fraction);
  }

  setMorph(fraction) {
    this.elts.text2.style.filter = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
    this.elts.text2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;

    fraction = 1 - fraction;
    this.elts.text1.style.filter = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
    this.elts.text1.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;

    this.elts.text1.textContent = this.texts[this.textIndex % this.texts.length];
    this.elts.text2.textContent = this.texts[(this.textIndex + 1) % this.texts.length];
  }

  doCooldown() {
    this.morph = 0;

    this.elts.text2.style.filter = "";
    this.elts.text2.style.opacity = "100%";

    this.elts.text1.style.filter = "";
    this.elts.text1.style.opacity = "0%";
  }
}
customElements.define('animated-title-morphing', AnimatedTitleMorphing);

class AnimatedTitleDistort extends HTMLElement {
  constructor() {
    let fontSize = 200;
    if (window.outerWidth > 1440) {
      fontSize = 360
    } else if (window.outerWidth > 1024) {
      fontSize = 260;
    }
    super()
    this.APP_CONFIG = {
      width: window.innerWidth,
      height: window.innerHeight,
      fontSize: fontSize,
      axisHelperSize: 10,
      text: ["KEEP ", "YOUR ", "HEAD ", "HIGH"],
    }

    this.time = 0;
    this.planeHeight = 50;
    this.ratio = this.APP_CONFIG.width / this.APP_CONFIG.height;
    this.planeWidth = this.planeHeight * this.ratio;

    //SET-UP CAMERA
    this.cameraOpts = {
      aspect: this.APP_CONFIG.width / this.APP_CONFIG.height,
      near: 0.1,
      far: 10000,
      z: this.planeHeight
    }

    let fov = 2 * Math.atan(this.planeHeight / (2 * this.cameraOpts.z)) * (180 / Math.PI);

    this.camera = new THREE.PerspectiveCamera(fov, this.cameraOpts.aspect, this.cameraOpts.near, this.cameraOpts.far);
    this.camera.position.z = this.cameraOpts.z;

    //SET-UP STAGE
    this.stage = new THREE.Scene();
    this.stage.add(this.camera);

    //SET-UP RENDERER
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(this.APP_CONFIG.width, this.APP_CONFIG.height);

    if (document.fonts) {
      this.tryToStart();
    } else {
      this.start();
    }
  }

  tryToStart() {
    if (document.fonts.check("20px unbounded")) {
      this.start();
    } else {
      setTimeout(() => {
        this.tryToStart();
      }, 300);
    }
  }

  start() {
    let texture = new THREE.Texture(this.createCanvas(this.APP_CONFIG.text));
    texture.premultiplyAlpha = false;
    texture.needsUpdate = true;

    let planeGeometry = new THREE.PlaneGeometry(this.planeWidth, this.planeHeight, 0, 0);
    this.uniforms = {
      texture: {
        type: 't',
        value: texture
      },
      percent: {
        type: 't',
        value: 0.1
      },
      time: {
        type: "f",
        value: this.time
      },
      mouseX: {
        type: "f",
        value: 0
      },
      mouseY: {
        type: "f",
        value: 0
      }
    }

    var vertShader = document.getElementById('distortVertexShader').innerHTML;
    var fragShader = document.getElementById('distortFragmentShader').innerHTML;

    let planeMaterial = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: vertShader,
      fragmentShader: fragShader,
      wireframe: false,
      wireframeLinewidth: 0,
      transparent: true,
      alphaTest: 0.1,
    });

    this.plane = new THREE.Mesh(planeGeometry, planeMaterial);
    this.stage.add(this.plane);

    let container = document.querySelector('.distort__stage');
    container.appendChild(this.renderer.domElement);
    gsap.ticker.add(() => this.animate());
    this.listen();
  }

  createCanvas(text) {
    let currentWidth = 0;
    const canvas = document.createElement('canvas');
    canvas.height = 4096;
    canvas.width = canvas.height * this.ratio;
    let context = canvas.getContext('2d');

    context.beginPath();

    context.fillStyle = 'rgba(255,255,255,0.001)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.font = 'Bold ' + this.APP_CONFIG.fontSize + 'px unbounded';
    context.fillStyle = '#FFF';
    let width = context.measureText(text).width;

    for (let i = 0; i < text.length; i++) {
      if (i == text.length - 1) {
        context.fillStyle = '#2e8bd6';
      }
      context.fillText(text[i], (canvas.width / 2 - width / 2) + currentWidth, canvas.height / 2);
      currentWidth += context.measureText(text[i]).width;
    }

    return canvas;
  }

  listen() {
    window.addEventListener('resize', (e) => this.onResize(e));
    let container = this.querySelector('.distort__canvas');
    container.addEventListener('mousemove', (e) => this.onMouseMove(e));
  }

  onResize(e) {
    this.APP_CONFIG.width =
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.updateProjectionMatrix();
  }

  onMouseMove(e) {
    this.mousePos = {
      x: e.clientX,
      y: e.clientY
    }
  }

  animate() {
    if (this.mousePos) {
      this.uniforms.mouseX.value = this.mousePos.x;
      this.uniforms.mouseY.value = window.innerHeight - this.mousePos.y;
    }

    this.renderer.render(this.stage, this.camera);
    this.uniforms.time.value += 0.1;
  }
}
customElements.define('animated-title-distort', AnimatedTitleDistort);

class CollectionPapers extends HTMLElement {
  constructor() {
    super()
    this.threshold = 990;
    this.columns = 3;
    this.elements = this.querySelectorAll('.collection__row-product');
    this._setup();
  }

  _setup() {
    this.elements.forEach(element => {
      element.addEventListener('mouseenter', (e) => this.onMouseEnter(e));
      element.addEventListener('mouseleave', (e) => this.onMouseLeave(e));
    });
  }

  onMouseEnter(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseEnterDesktop(e);
    }
    return;
  }

  onMouseLeave(e) {
    const innerWidth = window.innerWidth;
    if (innerWidth > this.threshold) {
      return this.onMouseLeaveDesktop(e);
    }
    return;
  }

  onMouseEnterDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;

    e.target.style.width = '40%';
    for (let i = start; i < start + this.columns; i++) {
      if (i != idx) {
        const element = this.elements[i];
        element.style.width = '30%';
      }
    }
  }

  onMouseLeaveDesktop(e) {
    const idx = e.target.dataset.index;
    const row = Math.floor(idx / this.columns);
    const start = row * this.columns;
    for (let i = start; i < start + this.columns; i++) {
      const element = this.elements[i];
      element.style.width = '33.33%';
    }
  }
}
customElements.define('collection-papers', CollectionPapers);

class FeaturedPapers extends HTMLElement {
  constructor() {
    super()
    this.wheel = document.querySelector('.wheel');
    this.elements = this.wheel.querySelectorAll('.wheel__paper');
    this.texts = document.querySelectorAll('.wheel__text h2');
    this.animate();
  }

  createWheel(radius) {
    const length = this.elements.length
    for (let i = 0; i < length; i++) {
      let rotation = i * (360 / length);
      gsap.set(this.elements[i], {
        left: "50%",
        top: "calc(50% - 50px)",
        transformOrigin: "left center",
        width: radius,
        rotation: -rotation,
      });
    }
  }

  animate() {
    this.createWheel(400);

    const totalDuration = 2000;
    const singleDuration = totalDuration / this.texts.length;

    let animation = gsap.timeline();
    ScrollTrigger.create({
      trigger: '.wheel__container',
      markers: false,
      start: 'center bottom',
      end: '+=' + totalDuration,
      pin: true,
      scrub: true,
      animation: animation
    });
    // Wheel-type animation
    animation.to(this.wheel, {
      rotation: 360,
      ease: 'none'
    });
    animation.to('.featured-papers .pin-spacer', {
      overflow: 'hidden'
    }, 0);

    this.texts.forEach((elem, i) => {
      const smallTimeline = gsap.timeline();
      const relevantText = this.querySelector('.wheel__text-' + i);

      ScrollTrigger.create({
        trigger: ".wheel__text",
        start: 'top+=' + (singleDuration * i) + ' center',
        end: "+=" + singleDuration,
        animation: smallTimeline,
        toggleActions: "play reverse play reverse",
      });

      smallTimeline
        .to(elem, { duration: 0.4, opacity: 1, ease: 'power3.inOut' }, 0)
    });

  }
}
customElements.define('featured-papers', FeaturedPapers);

class AboutUsLighter extends HTMLElement {
  constructor() {
    super()
    this.lighter = document.querySelector('.about-us-lighter .lighter__container');
    this.text = document.querySelector('.about-us-lighter .text__container');
    window.addEventListener('DOMContentLoaded', () => {
      this.animate();
    });
  }

  animate() {
    gsap.set(this.lighter, {
      left:"-15%",
      top:"150%",
      rotation: 20,
      xPercent: -50,
      yPercent: -50
    });
    gsap.set(this.text, {
      opacity: 0
    })

    gsap.timeline({
      scrollTrigger: {
        trigger: ".about-us-lighter__container",
        start: "top",
        pin: true,
        scrub: true,
      }
    })
    .to(this.text, {
      opacity: 1,
      duration: 2
    })
    .to(this.lighter, {
      left: "50%",
      top: "50%",
      duration: 3
    })
    .to(this.lighter, {
      left: "115%",
      top: "-50%",
      duration: 3
    }, 7)
  }
}
customElements.define('about-us-lighter', AboutUsLighter);

class AboutUsPapers extends HTMLElement {
  constructor() {
    super()
    this.papers = document.querySelectorAll('.about-us-papers .product__image');
    this.text = document.querySelector('.about-us-papers .text__block');
    window.addEventListener('DOMContentLoaded', () => {
      this.setupAnimation();
    });
  }

  setupAnimation() {
    const mm = gsap.matchMedia();

    mm.add("(max-width: 989px)", () => {
      this.mobile();
    });

    mm.add("(min-width: 990px)", () => {
      this.desktop();
    });
    /*ScrollTrigger.addEventListener("refreshInit", function () {
      ScrollTrigger.matchMedia({
        "(min-width: 990px)": this.mobile,
        "(max-width: 989px)": this.desktop
      });
    });*/
  }

  animate(totalDuration) {
    const displacement = 100 / this.papers.length;
    // Setting the papers spread
    this.papers.forEach((elem, i) => {
      gsap.set(elem, {
        top: displacement * i + '%'
      });
    });
   
    // Make invisible all texts
    gsap.set(this.text, {
      opacity: 0,
    })

    // Papers animation
    gsap.timeline({
      scrollTrigger: {
        trigger: '.about-us-papers__container',
        start: 'top',
        end: '+=' + totalDuration,
        pin: true,
        scrub: true
      }
    }, 0)
    .to(this.papers, {
      top: '50%',
    })
    .to(this.text, {
      duration: 0.1,
      opacity: 1,
      height: 'auto',
    }, 0)
  }

  mobile() {
    this.animate(this.dataset.totalDuration / 3);
  }

  desktop() {
    this.animate(this.dataset.totalDuration);
  }
}
customElements.define('about-us-papers', AboutUsPapers);

class FeaturedLighters extends HTMLElement {
  constructor() {
    super()
    this.features = this.querySelectorAll('.featured-lighters .feature__container');
    this.train = this.querySelector('.train');
    this.test = this.querySelector('.lighter-train.test');
    this.lighters = this.querySelector('.lighters__container');
    this.lighter = this.querySelector('.lighter-1');

    this.durationTotal = 10000;
    this.durationTrain = this.dataset.animationTrain;
    this.durationWindproof = this.dataset.animationWindproof;
    this.durationLighting = this.dataset.animationLighting;
    this.durationMultifunctional = this.dataset.animationMultifunctional;
    // this.durationMagic = this.dataset.animationMagic;
    this.durationTwist = this.dataset.animationTwist;
    this.durationRefillable = this.dataset.animationRefillable;
    this.durationDoobTube = this.dataset.animationDoobTube;

    const mm = gsap.matchMedia();
    window.addEventListener('DOMContentLoaded', () => {
      mm.add("(max-width: 989px)", () => {
        this.mobile();
      });
  
      mm.add("(min-width: 990px)", () => {
        this.desktop();
      });  
    });
  }

  mobile() {
    this.set()
    
    // Setting up the main pin section
    let animation = gsap.timeline();
    ScrollTrigger.create({
      trigger: ".featured-lighters__container",
      start: "top",
      end: '+=' + this.durationTotal,
      pin: true,
      scrub: true,
      animation: animation
    })

    // Dropping the lighter
    animation.fromTo('.lighter-train:not(.lighter-train__first)', {
      // top: '0%',
      yPercent: 400,
    }, {
      // top: '0',
      yPercent: 0,
    })

    animation.to(this.train, {
      xPercent: -100,
      duration: this.durationTrain,
    }, "train-in")
    /* Switching last item with lighters container */
    animation.to(this.lighters, {
      opacity: 1,
      duration: 0.01
    });
    animation.to(this.test, {
      opacity: 0,
      duration: 0.01
    });
    animation.to(this.train, {
      yPercent: -300,
    })
    // Train ending
    
    // Going to first stop - windproof
    animation.to(this.lighters, {
      left: "50%",
      duration: this.durationWindproof,
      // xPercent: 0,
      rotate: 354,
    }, 'moving')
    animation.to('.lighter-1', {
      opacity: 0,
    }, 'moving')
    animation.to('.lighter-1-bis', {
      opacity: 1,
    }, 'moving')
    animation.to('.feature-1', {
      opacity: 1,
    })
    animation.to('.lighter-1-bis', {
      opacity: 0,
      duration: 0.001,
    }, 'revealing')
    animation.to('.lighter-1-ter', {
      opacity: 1,
      duration: 0.001,
    }, 'revealing')
    
    // Flame animation
    animation.to('.featured-lighters .flame', {
      // duration: 1.5,
      // yPercent: 10,
      duration: this.durationLighting,
      opacity: 1,
    })
    
    animation.to('.feature-1', {
      opacity: 0,
    })

    // Going to second stop - magic stick
    animation.to('.featured-lighters .flame', {
      opacity: 0,
    }, "magic-stick")
    animation.to(this.lighters, {
      rotate: 370,
      duration: this.durationMultifunctional
    }, "magic-stick")
    animation.to(".lighter-1", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-1-bis", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-1-ter", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-2", {
      opacity: 1,
    }, "magic-stick")
    animation.to(".poker", {
      opacity: 1,
    }, "magic-stick")
    .to('.feature-2', {
      opacity: 1,
    })

    // Poker animation going out and on the left side
    .to('.poker', {
      yPercent: 60,
      duration: 1.5,
    })
    .to('.poker', {
      xPercent: -120,
      duration: 1.5,
    })
    .to('.poker', {
      yPercent: 0,
      duration: 1.5,
    })
    .to('.feature-2', {
      opacity: 0,
    })
    .to('.poker', {
      opacity: 0,
      duration: 0.01,
    })
    
    // Going to third stop - refillable
    animation.to(this.lighters, {
      rotate: 540,
      duration: this.durationTwist,
    }, "refillable")
    animation.to(".lighter-2", {
      opacity: 0,
    }, "refillable")
    animation.to(".lighter-3", {
      opacity: 1,
    }, "refillable")
    animation.to('.feature-3', {
      opacity: 1,
    })
    
    
     // Gas refill coming in
        animation.to(".featured-lighters .refill", {
            xPercent: -50,
            duration: this.durationRefillable,
        });
        animation.to(".feature-3", {
            opacity: 0,
        });

        // Create a specific position for the doob tube swap
        animation.add("doob-position");

        // Quick hide of refill elements
        animation.to(
            [".lighter-3", ".featured-lighters .refill"],
            {
                opacity: 0,
                duration: 0.01,
            },
            "doob-position"
        );

        // Ensure we're at the right position and show doob tube immediately
        animation.to(
            this.lighters,
            {
                left: "75%",
                duration: 0.01,
                scale: 1, // Increased from 1.2 to 2 for double size
                rotation: 0,
            },
            "doob-position"
        );

        animation.to(
            ".doob-tube",
            {
                opacity: 1,
                duration: 0.01,
            },
            "doob-position"
        );

        // Then smoothly animate the movement from right to left with rotation
        animation.to(
            this.lighters,
            {
                left: "50%",
                rotation: 370,
                duration: this.durationTwist,
                ease: "none",
            },
            "doob-position+=0.01"
        );

        // Show the feature text much sooner
        animation.to(
            ".feature-4",
            {
                opacity: 1,
            },
            "doob-position+=3"
        );

    // Add new animation for doob-filled with better timing
        animation.add("doob-filled-entry", "doob-position+=4.5"); // Start after doob-tube is in place

        // Set initial position at left edge of page
        animation.set(
            ".doob-filled",
            {
                left: "-200px",
                opacity: 0,
                y: "-50%",
            },
            "doob-filled-entry"
        );

        // Bring in the filled version from left smoothly
        animation.to(
            ".doob-filled",
            {
                left: "calc(10% - 150px)",
                opacity: 1,
                duration: 2,
                ease: "power1.inOut",
            },
            "doob-filled-entry"
        );
    

  }

  set() {
    gsap.set(this.train, {
      xPercent: -100,
      left: 'calc(50vw - 4vw - ' + 72 / 2 + 'px)',
    });
    const bbox = this.test.getBoundingClientRect();
    gsap.set(this.train, {
      xPercent: 0,
    });
    gsap.set(this.lighters, {
      left: ((bbox.x +  bbox.width / 2) / window.innerWidth) * 100 + '%',
      top: '50%',
      xPercent: -50,
      yPercent: -50,
      opacity: 0,
      width: '72px',
    });
    gsap.set(this.features, {
      opacity: 0,
    });
    gsap.set('.featured-lighters .refill', {
      xPercent: -2000,
      left: '50%',
    });
    gsap.set('.featured-lighters .poker', {
      yPercent: 0,
    });
  }

  desktop() {
    this.set();
    
    // Setting up the main pin section
    let animation = gsap.timeline();
    ScrollTrigger.create({
      trigger: ".featured-lighters__container",
      start: "top",
      end: '+=' + this.durationTotal,
      pin: true,
      scrub: true,
      animation: animation,
      invalidateOnRefresh: true,
    })

    // Dropping the lighter
    animation.fromTo('.lighter-train:not(.lighter-train__first)', {
      // top: '0%',
      yPercent: 400,
    }, {
      // top: '0',
      yPercent: 0,
    })

    animation.to(this.train, {
      xPercent: -100,
      duration: this.durationTrain,
    }, "train-in")
    /* Switching last item with lighters container */
    animation.to(this.lighters, {
      opacity: 1,
      duration: 0.01
    });
    animation.to(this.test, {
      opacity: 0,
      duration: 0.01
    });
    animation.to(this.train, {
      yPercent: -300,
    })
    // Train ending
    
    // Going to first stop - windproof
    animation.to(this.lighters, {
      left: "80%",
      xPercent: 0,
      rotate: 354,
      duration: this.durationWindproof
    }, 'moving')
    animation.to('.lighter-1', {
      opacity: 0,
    }, 'moving')
    animation.to('.lighter-1-bis', {
      opacity: 1,
    }, 'moving')
    animation.to('.feature-1', {
      opacity: 1,
    })
    animation.to('.lighter-1-bis', {
      opacity: 0,
      duration: 0.001,
    }, 'revealing')
    animation.to('.lighter-1-ter', {
      opacity: 1,
      duration: 0.001,
    }, 'revealing')
    
    // Flame animation
    animation.to('.featured-lighters .flame', {
      // duration: 1.5,
      // yPercent: 10,
      duration: this.durationLighting,
      opacity: 1,
    })
    
    animation.to('.feature-1', {
      opacity: 0,
    })

    // Going to second stop - magic stick
    animation.to('.featured-lighters .flame', {
      opacity: 0,
    }, "magic-stick")
    animation.to(this.lighters, {
      left: "20%",
      top: "50%",
      rotate: 370,
      duration: this.durationMultifunctional
    }, "magic-stick")
    animation.to(".lighter-1", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-1-bis", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-1-ter", {
      opacity: 0,
    }, "magic-stick")
    animation.to(".lighter-2", {
      opacity: 1,
    }, "magic-stick")
    animation.to(".poker", {
      opacity: 1,
    }, "magic-stick")
    .to('.feature-2', {
      opacity: 1,
    })

    // Poker animation going out and on the left side
    .to('.poker', {
      yPercent: 60,
      duration: 1,
    })
    .to('.poker', {
      xPercent: -120,
    })
    .to('.poker', {
      yPercent: 0,
    })
    .to('.feature-2', {
      opacity: 0,
    })
    .to('.poker', {
      opacity: 0,
      duration: 0.01,
    })
    
    // Going to third stop - refillable
    animation.to(this.lighters, {
      left: "75%",
      rotate: 540,
      duration: this.durationTwist,
    }, "refillable")
    animation.to(".lighter-2", {
      opacity: 0,
    }, "refillable")
    animation.to(".lighter-3", {
      opacity: 1,
    }, "refillable")
    animation.to('.feature-3', {
      opacity: 1,
    })


      // Gas refill coming in
        animation.to(".featured-lighters .refill", {
            xPercent: -50,
            duration: this.durationRefillable,
        });
        animation.to(".feature-3", {
            opacity: 0,
        });

        // Create a specific position for the doob tube swap
        animation.add("doob-position");

        // Quick hide of refill elements
        animation.to(
            [".lighter-3", ".featured-lighters .refill"],
            {
                opacity: 0,
                duration: 0.01,
            },
            "doob-position"
        );

        // Ensure we're at the right position and show doob tube immediately
        animation.to(
            this.lighters,
            {
                left: "75%",
                duration: 0.01,
                scale: 1.5, // Increased from 1.2 to 2 for double size
                rotation: 0,
            },
            "doob-position"
        );

        animation.to(
            ".doob-tube",
            {
                opacity: 1,
                duration: 0.01,
            },
            "doob-position"
        );

        // Then smoothly animate the movement from right to left with rotation
        animation.to(
            this.lighters,
            {
                left: "20%",
                rotation: 370,
                duration: this.durationTwist,
                ease: "none",
            },
            "doob-position+=0.01"
        );

        // Show the feature text much sooner
        animation.to(
            ".feature-4",
            {
                opacity: 1,
            },
            "doob-position+=3" // Significantly reduced wait time
        );

         // Add new animation for doob-filled with better timing
        animation.add("doob-filled-entry", "doob-position+=4.5"); // Start after doob-tube is in place

        // Set initial position at left edge of page
        animation.set(
            ".doob-filled",
            {
                left: "-200px",
                opacity: 0,
                y: "-50%",
            },
            "doob-filled-entry"
        );

        // Bring in the filled version from left smoothly
        animation.to(
            ".doob-filled",
            {
                left: "calc(10% - 200px)",
                opacity: 1,
                duration: 2,
                ease: "power1.inOut",
            },
            "doob-filled-entry"
        );

  }
}
customElements.define('featured-lighters', FeaturedLighters);

class FeaturedPapersNew extends HTMLElement {
  constructor() {
    super()
    this.wheel = document.querySelector('.wheel');
    this.elements = this.wheel.querySelectorAll('.wheel__paper');
    this.texts = document.querySelectorAll('.wheel__text h2');

    this.totalDuration = this.dataset.totalDuration;

    const mm = gsap.matchMedia();
    window.addEventListener('DOMContentLoaded', () => {
      mm.add("(max-width: 989px)", () => {
        this.mobile();
      });
  
      mm.add("(min-width: 990px)", () => {
        this.desktop();
      });
    });
  }

  mobile() {
    // Create the wheel
    const length = this.elements.length
    for (let i = 0; i < length; i++) {
      let rotation = i * (360 / length);
      gsap.set(this.elements[i], {
        left: "50%",
        top: "calc(50% - 50px)",
        transformOrigin: "left center",
        width: 400,
        rotation: -rotation,
      });
    }

    const singleDuration = this.totalDuration / this.texts.length;

    let animation = gsap.timeline();
    ScrollTrigger.create({
      trigger: '.wheel__container',
      start: 'center bottom',
      end: '+=' + this.totalDuration,
      pin: true,
      scrub: true,
      animation: animation
    });
    // Wheel-type animation
    animation.to(this.wheel, {
      rotation: 360,
      ease: 'none'
    });
    animation.to('.featured-papers .pin-spacer', {
      overflow: 'hidden'
    }, 0);

    this.texts.forEach((elem, i) => {
      const smallTimeline = gsap.timeline();
      const relevantText = this.querySelector('.wheel__text-' + i);

      ScrollTrigger.create({
        trigger: ".wheel__text",
        start: 'top+=' + (singleDuration * i) + ' center',
        end: "+=" + singleDuration,
        animation: smallTimeline,
        toggleActions: "play reverse play reverse",
      });

      smallTimeline
        .to(elem, { duration: 0.4, opacity: 1, ease: 'power3.inOut' }, 0)
    });

  }

  desktop() {
    // Create the wheel
    const length = this.elements.length
    for (let i = 0; i < length; i++) {
      let rotation = i * (360 / length);
      gsap.set(this.elements[i], {
        left: "50%",
        top: "calc(50% - 50px)",
        transformOrigin: "center left",
        width: 400,
        rotation: -rotation,
      });
    }

    const singleDuration = this.totalDuration / this.texts.length;

    let animation = gsap.timeline();
    ScrollTrigger.create({
      trigger: '.wheel__container',
      start: 'center-=30 bottom',
      end: '+=' + this.totalDuration,
      pin: true,
      scrub: true,
      animation: animation
    });
    // Wheel-type animation
    animation.to(this.wheel, {
      rotation: 360,
      ease: 'none'
    });
    animation.to('.featured-papers .pin-spacer', {
      overflow: 'hidden'
    }, 0);

    this.texts.forEach((elem, i) => {
      const smallTimeline = gsap.timeline();
      const relevantText = this.querySelector('.wheel__text-' + i);

      ScrollTrigger.create({
        trigger: ".wheel__text",
        start: 'top+=' + (singleDuration * i) + ' center',
        end: "+=" + singleDuration,
        animation: smallTimeline,
        toggleActions: "play reverse play reverse",
      });

      smallTimeline
        .to(elem, { duration: 0.4, opacity: 1, ease: 'power3.inOut' }, 0)
    });

  }
}
customElements.define('featured-papers-new', FeaturedPapersNew);

class ContactMarquee extends HTMLElement {
    constructor() {
        super()
        this._setup();
    }

    _setup() {
        const elements = gsap.utils.toArray("contact-marquee .marquee__item");
        const loop = horizontalLoop(elements, {
            paused: false,
            repeat: -1,
            speed: 2 
        });
    }
}

customElements.define('contact-marquee', ContactMarquee);